import { useState, useEffect } from "react";
import "./CallInputPanel.css";
import { CALL_TYPES } from "../constants/call_types";
import { LLM_MODELS } from "../constants/llm_models";

function CallInputPanel({
  languages,
  languagesLoading,
  selectedCountry,
  onCountryChange,
  fileName,
  onFileChange,
  onStart,
  collapsed,
  compact,
  onToggleCollapse,
}) {
  const [callType, setCallType] = useState("overdue");
  const [voice, setVoice] = useState("");
  const [llm, setLlm] = useState("");
  const [voices, setVoices] = useState([]);
  const [voicesLoading, setVoicesLoading] = useState(false);

  useEffect(() => {
    if (!selectedCountry) return;

    const loadVoices = async () => {
      try {
        setVoicesLoading(true);
        const resp = await fetch(
          `/api/get-voices?language=${encodeURIComponent(selectedCountry)}`
        );
        const data = await resp.json().catch(() => null);

        if (!resp.ok || !data?.success) {
          throw new Error(data?.error || `Failed to load voices (${resp.status})`);
        }

        const list = Array.isArray(data.voices) ? data.voices : [];
        setVoices(list);

        const exists = list.some((v) => v.id === voice);
        if (!exists) setVoice("");
      } catch (e) {
        console.error("get-voices failed:", e);
        setVoices([]);
        setVoice("");
      } finally {
        setVoicesLoading(false);
      }
    };

    loadVoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountry]);

  const canStart = !!fileName && !!selectedCountry;

  const handleStart = () => {
    if (!canStart) return;

    onStart?.({
      mode: "file",
      country: selectedCountry,
      callType,
      voice,
      llm,
      fileName,
    });
  };

  if (collapsed) {
    return (
      <aside className="call-input-collapsed" onClick={onToggleCollapse}>
        <span className="call-input-collapsed-label">Call input</span>
      </aside>
    );
  }

  return (
    <section className={`call-input-panel ${compact ? "compact" : ""}`}>
      <header className="panel-header">
        <div>
          <h2>Call input</h2>
          <p>Define which accounts Sofia should call.</p>
        </div>

        <div className="header-actions">
          <button
            className="start-run-btn"
            onClick={handleStart}
            disabled={!canStart}
            title={!canStart ? "Upload an Excel file to start" : "Start run"}
          >
            ▶ Start run
          </button>
        </div>
      </header>

      <div className="panel-section">
        <h3 className="section-title">Call settings</h3>

        <div className="field-row">
          <Field label="Language">
            <select
              value={selectedCountry}
              onChange={onCountryChange}
              className="select-input"
              disabled={languagesLoading}
            >
              {languagesLoading ? (
                <option value={selectedCountry}>Loading languages…</option>
              ) : (
                languages.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.name} ({l.code})
                  </option>
                ))
              )}
            </select>
          </Field>

          <Field label="Call type">
            <select
              value={callType}
              onChange={(e) => setCallType(e.target.value)}
              className="select-input"
            >
              {CALL_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="field-row">
          <Field label="Voice">
            <select
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              className="select-input"
              disabled={voicesLoading}
            >
              <option value="">
                {voicesLoading ? "Loading voices…" : "Select voice"}
              </option>
              {voices.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} — {v.description}
                </option>
              ))}
            </select>
          </Field>

          <Field label="LLM">
            <select
              value={llm}
              onChange={(e) => setLlm(e.target.value)}
              className="select-input"
            >
              <option value="">Select LLM</option>
              {LLM_MODELS.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </div>

      <div className="panel-section">
        <h3 className="section-title">Call data</h3>

        <label className="field-label">Excel with call IDs</label>

        <div className="file-actions-row">
          <label className="file-input-label">
            <span>{fileName ? "Change Excel file" : "Choose Excel file"}</span>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) onFileChange?.(f);
              }}
            />
          </label>
        </div>

        <div className="file-helper-row">
          <a
            href="/assets/sofia_calls_template.xlsx"
            download
            className="template-link subtle"
          >
            Download template
          </a>

          <span className="file-helper-hint">
            {fileName ? "Ready to start." : "Don’t have a file yet? Use the template."}
          </span>
        </div>

        <div className="file-info">
          <strong>File:</strong> {fileName || "No file selected"}
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <div className="field-group">
      <label className="field-label">{label}</label>
      {children}
    </div>
  );
}

export default CallInputPanel;
